import { useMemo } from 'react'
import type { User } from '../../../models'

interface UsePermissionsProps {
  currentUser: User | null
}

export interface ProcessPermissions {
  canViewProcessos: boolean
  canCreateProcessos: boolean
  canEditProcessos: boolean
  canDeleteProcessos: boolean
  canViewSetores: boolean
  canCreateSetores: boolean
  canEditSetores: boolean
  canDeleteSetores: boolean
  canViewEquipes: boolean
  canCreateEquipes: boolean
  canEditEquipes: boolean
  canDeleteEquipes: boolean
  canViewAutorizadores: boolean
  canCreateAutorizadores: boolean
  canEditAutorizadores: boolean
  canDeleteAutorizadores: boolean
  canManageUsers: boolean
  isAdmin: boolean
  isGestor: boolean
  isUserNormal: boolean
  setorId?: number | undefined
}

export const usePermissions = ({ currentUser }: UsePermissionsProps): ProcessPermissions => {
  return useMemo(() => {
    if (!currentUser) {
      return {
        canViewProcessos: false,
        canCreateProcessos: false,
        canEditProcessos: false,
        canDeleteProcessos: false,
        canViewSetores: false,
        canCreateSetores: false,
        canEditSetores: false,
        canDeleteSetores: false,
        canViewEquipes: false,
        canCreateEquipes: false,
        canEditEquipes: false,
        canDeleteEquipes: false,
        canViewAutorizadores: false,
        canCreateAutorizadores: false,
        canEditAutorizadores: false,
        canDeleteAutorizadores: false,
        canManageUsers: false,
        isAdmin: false,
        isGestor: false,
        isUserNormal: false,
      }
    }

    // Determinar tipo de usuário baseado na função global (funcao_usuario)
    // ADMIN e DIRETOR têm acesso total
    const isAdmin = currentUser.funcao_usuario === 'ADMIN' || currentUser.funcao_usuario === 'DIRETOR'
    
    // Verificar se é GESTOR através da tabela usuarios_setores (pivot)
    // NORMALIZAÇÃO: Converte 0/1 para false/true usando Boolean()
    const isGestor = currentUser.setores?.some(
      setor => setor.pivot?.funcao === 'GESTOR' && Boolean(setor.pivot?.active)
    ) || false
    
    // USUARIO comum: não é ADMIN, nem DIRETOR, nem GESTOR
    const isUserNormal = !isAdmin && !isGestor

    // Definir permissões baseadas no tipo de usuário
    // ADMIN: Acesso total a tudo
    // GESTOR: Pode ver e gerenciar processos, setores, equipes (do seu contexto)
    // USUARIO: Apenas visualiza processos do seu setor, sem acesso a gestão de setores/equipes
    const permissions: ProcessPermissions = {
      // Processos - Todos podem visualizar (filtrados por setor), apenas GESTOR/ADMIN podem criar/editar/deletar
      canViewProcessos: true, // Todos podem ver processos (filtrados por setor para usuários normais)
      canCreateProcessos: isAdmin || isGestor,
      canEditProcessos: isAdmin || isGestor,
      canDeleteProcessos: isAdmin || isGestor,
      
      // Setores - Apenas ADMIN e GESTOR podem visualizar e gerenciar
      canViewSetores: isAdmin || isGestor,
      canCreateSetores: isAdmin,
      canEditSetores: isAdmin,
      canDeleteSetores: isAdmin,
      
      // Equipes - Apenas ADMIN e GESTOR podem visualizar e gerenciar
      canViewEquipes: isAdmin || isGestor,
      canCreateEquipes: isAdmin || isGestor,
      canEditEquipes: isAdmin || isGestor,
      canDeleteEquipes: isAdmin || isGestor,
      
      // Autorizadores - Apenas ADMIN pode visualizar e gerenciar
      canViewAutorizadores: isAdmin,
      canCreateAutorizadores: isAdmin,
      canEditAutorizadores: isAdmin,
      canDeleteAutorizadores: isAdmin,
      
      // Gerenciamento de usuários - Apenas ADMIN e GESTOR
      canManageUsers: isAdmin || isGestor,
      
      // Tipos de usuário
      isAdmin,
      isGestor,
      isUserNormal,
      
      // Setor do usuário (para filtrar processos)
      setorId: currentUser.setor_logado_fk || currentUser.setor_usuario_fk,
    }

    return permissions
  }, [currentUser])
}
